import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getExpectedUserType } from "../utils/authScope";
import { UserType } from "../api/authService";
import { collectVisitorInfo } from "../utils/visitorInfo";
import { recordVisitorSession, recordPageView } from "../api/visitorService";

const SESSION_FLAG = "bigmarket_visit_recorded";

function alreadyRecordedThisSession() {
  try {
    return sessionStorage.getItem(SESSION_FLAG) === "1";
  } catch {
    return false;
  }
}

function markRecorded() {
  try {
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    // ignore
  }
}

// Renders nothing. On the client-facing site it (1) records device/browser/referrer info once per
// browser session and (2) logs every page view. No guest user is created just for opening the site -
// actions like add-to-cart create one on demand via ensureSession. The server only keeps anonymous
// visitors located in Israel. Staff areas (admin, business owner, delivery) are skipped.
export default function VisitorTracker() {
  const { pathname } = useLocation();
  const sessionStarted = useRef(false);
  const previousPath = useRef(null);

  useEffect(() => {
    if (getExpectedUserType(pathname) !== UserType.User) return;

    (async () => {
      // The session must go first so the server knows whether this device is tracked.
      if (!sessionStarted.current) {
        sessionStarted.current = true;
        if (!alreadyRecordedThisSession()) {
          markRecorded();
          await recordVisitorSession(collectVisitorInfo());
        }
      }

      // In-app navigation's "referrer" is the previous page; the first view uses the external one.
      const referrer = previousPath.current ?? collectVisitorInfo().referrer;
      previousPath.current = pathname;
      recordPageView(pathname, referrer);
    })();
  }, [pathname]);

  return null;
}
